const VALID_ROLES = ["system", "user", "assistant"];
const MAX_REPORTED_ERRORS = 10;

const isMessageShape = (msg, line, errors, prefix) => {
  if (!msg || typeof msg !== "object") {
    errors.push(`Line ${line}: ${prefix} entry is not an object`);
    return false;
  }
  if (!msg.role || !VALID_ROLES.includes(msg.role)) {
    errors.push(
      `Line ${line}: ${prefix} has missing or invalid role "${msg?.role}" (expected system|user|assistant)`,
    );
    return false;
  }
  if (msg.content == null || (typeof msg.content !== "string" && !Array.isArray(msg.content))) {
    errors.push(`Line ${line}: ${prefix} is missing content`);
    return false;
  }
  return true;
};

const validateMessagesArray = (messages, line, errors, prefix) => {
  if (!Array.isArray(messages)) {
    errors.push(`Line ${line}: ${prefix} must be an array of messages`);
    return false;
  }
  if (messages.length === 0) {
    errors.push(`Line ${line}: ${prefix} is empty`);
    return false;
  }
  let ok = true;
  messages.forEach((msg, i) => {
    if (!isMessageShape(msg, line, errors, `${prefix}[${i}]`)) ok = false;
  });
  return ok;
};

const validateLineSFT = (obj, line, errors) => {
  if (!obj || typeof obj !== "object") {
    errors.push(`Line ${line}: not a JSON object`);
    return false;
  }
  if (!obj.messages) {
    errors.push(`Line ${line}: missing "messages"`);
    return false;
  }
  return validateMessagesArray(obj.messages, line, errors, "messages");
};

const validateLineDPO = (obj, line, errors) => {
  if (!obj || typeof obj !== "object") {
    errors.push(`Line ${line}: not a JSON object`);
    return false;
  }
  let ok = true;
  if (!obj.input || !obj.input.messages) {
    errors.push(`Line ${line}: missing "input.messages"`);
    ok = false;
  } else if (!validateMessagesArray(obj.input.messages, line, errors, "input.messages")) {
    ok = false;
  }
  if (!Array.isArray(obj.preferred_output)) {
    errors.push(`Line ${line}: "preferred_output" must be an array`);
    ok = false;
  } else if (obj.preferred_output.length === 0) {
    errors.push(`Line ${line}: "preferred_output" is empty`);
    ok = false;
  } else if (!validateMessagesArray(obj.preferred_output, line, errors, "preferred_output")) {
    ok = false;
  }
  if (!Array.isArray(obj.non_preferred_output)) {
    errors.push(`Line ${line}: "non_preferred_output" must be an array`);
    ok = false;
  } else if (obj.non_preferred_output.length === 0) {
    errors.push(`Line ${line}: "non_preferred_output" is empty`);
    ok = false;
  } else if (
    !validateMessagesArray(obj.non_preferred_output, line, errors, "non_preferred_output")
  ) {
    ok = false;
  }
  return ok;
};

const VALID_CHECKER_TYPES = ["pytest", "exit_code", "file_exists", "script"];

const validateLineRL = (obj, line, errors) => {
  if (!obj || typeof obj !== "object") {
    errors.push(`Line ${line}: not a JSON object`);
    return false;
  }
  const spec = obj.task_spec;
  if (!spec || typeof spec !== "object") {
    errors.push(`Line ${line}: missing "task_spec" object`);
    return false;
  }
  let ok = true;
  if (!spec.prompt || typeof spec.prompt !== "string" || !spec.prompt.trim()) {
    errors.push(`Line ${line}: task_spec.prompt must be a non-empty string`);
    ok = false;
  }
  const env = spec.env || "tfcode";
  if (env !== "tfcode") {
    errors.push(
      `Line ${line}: task_spec.env "${env}" is not supported (v1: tfcode only)`,
    );
    ok = false;
  }
  const maxSteps = spec.max_steps === undefined ? 16 : spec.max_steps;
  if (!Number.isInteger(maxSteps) || maxSteps < 1 || maxSteps > 64) {
    errors.push(
      `Line ${line}: task_spec.max_steps must be an integer between 1 and 64`,
    );
    ok = false;
  }
  const checker = spec.checker;
  if (!checker || typeof checker !== "object") {
    errors.push(`Line ${line}: missing "task_spec.checker" object`);
    return false;
  }
  if (!VALID_CHECKER_TYPES.includes(checker.type)) {
    errors.push(
      `Line ${line}: task_spec.checker.type must be one of ${VALID_CHECKER_TYPES.join("|")}`,
    );
    ok = false;
  }
  if (!checker.config || typeof checker.config !== "object") {
    errors.push(`Line ${line}: missing "task_spec.checker.config" object`);
    ok = false;
  } else if (checker.type === "pytest" && !Array.isArray(checker.config.test_paths)) {
    errors.push(`Line ${line}: checker.config.test_paths must be an array for pytest`);
    ok = false;
  } else if (checker.type === "exit_code" && !checker.config.command) {
    errors.push(`Line ${line}: checker.config.command is required for exit_code`);
    ok = false;
  } else if (checker.type === "file_exists" && !Array.isArray(checker.config.paths)) {
    errors.push(`Line ${line}: checker.config.paths must be an array for file_exists`);
    ok = false;
  } else if (checker.type === "script" && !checker.config.script) {
    errors.push(`Line ${line}: checker.config.script is required for script`);
    ok = false;
  }
  if (
    spec.setup_files !== undefined &&
    (typeof spec.setup_files !== "object" || Array.isArray(spec.setup_files))
  ) {
    errors.push(
      `Line ${line}: task_spec.setup_files must be an object of { relativePath: contents }`,
    );
    ok = false;
  }
  if (
    spec.setup_commands !== undefined &&
    (!Array.isArray(spec.setup_commands) ||
      !spec.setup_commands.every((c) => typeof c === "string"))
  ) {
    errors.push(
      `Line ${line}: task_spec.setup_commands must be an array of strings`,
    );
    ok = false;
  }
  return ok;
};

const validateTrainingJsonl = (text, type) => {
  const errors = [];
  let totalCount = 0;
  let validCount = 0;

  if (!text || text.trim() === "") {
    return {
      isValid: false,
      errors: ["File is empty"],
      validCount: 0,
      totalCount: 0,
    };
  }

  const lines = text.split(/\r?\n/);
  const validator =
    type === "generativeTraining"
      ? validateLineDPO
      : type === "rlTask"
        ? validateLineRL
        : validateLineSFT;

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    if (!raw || raw.trim() === "") continue;
    const line = i + 1;
    totalCount++;

    let obj;
    try {
      obj = JSON.parse(raw);
    } catch (e) {
      errors.push(`Line ${line}: invalid JSON - ${e.message}`);
      continue;
    }

    if (validator(obj, line, errors)) validCount++;
    if (errors.length >= MAX_REPORTED_ERRORS) break;
  }

  return {
    isValid: totalCount > 0 && errors.length === 0,
    errors,
    validCount,
    totalCount,
  };
};

export default validateTrainingJsonl;
